import React from "react";
import {PocMap} from './PocMap'
import BigHeader from '../../components/BigHeader';

const Proof = () => {
    return (
        <div>
			<BigHeader headerText={'Proof Of Concept'} backgroundImage={'https://static.igem.wiki/teams/4602/wiki/images/images/background.jpg'}/>
            <div style={{margin: '53px'}}>
			<h1 id="theory-text" className="">Theory</h1>
			<p id="5e190c57-e77b-4185-9107-0539cb1c9d40" className="">Our design principle expands on the pre-existing
				virus-trapping capacities of the natural glycoprotein mucin. To facilitate this, we have conceptualised
				a two-way intermediary binding protein which acts as a glue, enhancing stickiness of the pathogen and
				mucin to each other. With the increase in particle size of pathogen-binder, or mucin-binder complexes
				through abundant binder proteins in the viscous solution, we hope to increase the diffusion coefficient
				of the particles such that even with non-permanent, transient binding between individual proteins, we
				can effectively immobilise and reduce the rate of cell entry of the pathogens.</p>
			<p id="36002172-c57b-4814-96d6-cfcbb7eacacf" className="">By having a constant solid base made of the mucin
				binding protein (from those in nature) and a flexible linker which increases the binding range of the
				two domains, and a modularly interchangeable pathogen-binding domain, we hope to show that we can
				effectively and efficiently generate a library of binding proteins tailored to individual pathogens,
				potentially not limited by species, genus, or even domain of the pathogen.</p>
			<PocMap/>
			<h2 id="model-target-identification-text" className="">Model target identification</h2>
			<p id="2143ff83-958f-48c3-994c-d6277ac7a528" className="">The first step of the pipeline, like any scientific
				project, begins with a thorough literature search.</p>
			<p id="4a4662d2-7cae-4a17-b885-8aa2f02e5ab3" className="">We identify potential target pathogens, and select
				outward-facing proteins that may be good targets for binding. It is vital at this point to have a good
				understanding of the protein / pathogen in question. In general, structurally important proteins and
				residues are likely highly conserved across different pathogen strains within the same species, but may
				not be ideal binding targets. On the other hand, the infective machinery of pathogens may have
				structural characteristics that make them ideal binding targets, but are under higher evolutionary
				pressure and are more structurally variable. We choose multiple targets for each pathogen, taking into
				consideration their structure and role.</p>
			<p id="eef93959-7c7a-401e-990a-5b693caf7904" className="">For each protein target, we must also identify
				specific sites which may facilitate strong binding. General conditions are: structural grooves, and
				charged / hydrophobic residues which facilitate strong interactions.</p>
			<p id="b91a1606-e8ed-45be-8af8-602b6d5af397" className="">
			</p>
			<h2 id="rfdiffusion-text" className="">RFdiffusion protein design and in-silico analysis
			</h2>
			<p id="60d9fba4-13d4-4600-b70b-ad56b50d9dc7" className="">(<em><em><em> <a href="https://2023.igem.wiki/paris-bettencourt/silico-model">Link to RFdiffusion</a> page for more
							details)</em></em></em></p>
			<p id="da48f1ad-f199-4def-a4d5-4bf4a0228c29" className="">Once the targets are chosen, we run RFdiffusion
				simulations to obtain a library of potential binding proteins. RFdiffusion itself returns a
				RosettaFold-based score for each protein which predict its likelihood of correct folding, but we perform
				additional in-silico analysis to further select for the best candidates.</p>
			<p id="8af73db5-8556-4341-b0bb-c084836fb2de" className="">In our case we opted for the use of HDOCK, which,
				given two PDB structures, returns a score based on the likelihood of binding interaction between the two
				proteins.</p>
			<h2 id="protein-expression-text" className="">Protein expression and purification</h2>
			<p id="ab086eac-2cce-4e6e-bd79-cc5b2160fbe1" className="">The best candidates based on in-silico folding and
				docking analysis are then prepared for expression in bacteria and subsequent protein purification.</p>
			<ol type="1" id="4eb805b4-51e1-4f50-a6d6-2a187e4ead07" className="numbered-list" start="1">
				<li>Hypothetical protein amino acid sequences are codon optimised for expression in
					<em><em><em><em><em><em><em>E. coli</em></em></em></em></em></em></em> using Salis lab’s online CDS
					optimisation tool.<ol type="a" id="a23259a4-951e-4def-bc1d-26aa32459794" className="numbered-list"
						start="1">
						<li>RFdiffusion proteins are unnatural, and often the sequences are highly repetetive, leading
							to some issues both at the protein level and nucleotide level (reduced mRNA stability). To
							try to work around this, we focus the codon optimisation priorities around avoiding
							repetitive sequence elements. </li>
					</ol>
					<ol type="a" id="f4b5ed7c-3843-4b19-b668-c2db37ae62c8" className="numbered-list" start="2">
						<li>Optimised sequences often still fail third party gene synthesis checks (twist, IDT etc.),
							longer sequences are sometimes not physically possible to synthesise, and we discount them
							from proceeding the pipeline.</li>
					</ol>
				</li>
			</ol>
			<ol type="1" id="7762cd60-4f86-427e-9153-9dc2fb4d5fd3" className="numbered-list" start="2">
				<li>Optimised sequences are obtained through gene synthesis, complete with MoClo compatible prefix and
					suffixes.</li>
			</ol>
			<ol type="1" id="3bc50905-0e1a-48a4-b008-ac7560d0b0b1" className="numbered-list" start="3">
				<li>Protein coding sequences are cloned via golden gate into expression vectors (<a
						href="http://parts.igem.org/Part:BBa_K4602420">BBa_K4602420</a>).</li>
			</ol>
			<ol type="1" id="8719779d-ecb0-4d61-a03c-8a71bb218f20" className="numbered-list" start="4">
				<li>The expression plasmids are cloned into plasmid-producing strain of <em><em><em><em><em><em>E.
											coli</em></em></em></em></em></em> (TOP10). Amplified plasmid are harvested
					via miniprep, and re-transformed into a protein-producing strain of
					<em><em><em><em><em><em><em><em>E. coli </em></em></em></em></em></em></em></em>(<em>Rosetta
						BL21</em>).</li>
			</ol>
			<ol type="1" id="ea3f6f09-d405-490f-ade8-9929bed6b3d2" className="numbered-list" start="5">
				<li>Transformed cells are incubated, and target proteins are purified.</li>
			</ol>
			<p id="d6b07dd2-2d12-4005-9252-62384bd4bf78" className="">
			</p>
			<h2 id="protein-binding-text" className="">Protein binding assessment</h2>
			<p id="8ae39780-7ce9-4bd6-87b6-08fbe727e8c4" className="">Binding assessment is performed through multiple
				mediums. As we do not know the potential strength of interactions, it is advised to perform multiple
				analysis with different conditions. For preliminary analysis, we conduct binding analysis both at
				neutral pH, and an acidic pH similar to that of the vaginal canal.</p>
			<ul id="eae06586-0256-423e-a7a9-5d836363095f" className="toggle">
				<li>
					<details open="">
						<summary>Pull-down assay</summary>
						<p id="6b699c83-3ffc-4f01-b3de-c0b774a6ac24" className="">A simple binding assay to measure
							protein-protein interaction. Purification-tagged “Bait” proteins are incubated with “pray”
							proteins which lack a purification tags. These are passed through an affinity purification
							column, and analysed for their interaction.</p>
					</details>
				</li>
			</ul>
			<ul id="ab6c41cf-187b-49cc-90f2-91c97b5fb334" className="toggle">
				<li>
					<details open="">
						<summary>ELISA-like binding assay</summary>
						<p id="3674eb93-01fb-444d-be8a-042bbb330431" className="">96-well plates for ELISA have chemically
							altered polystyrene which bind readily to proteins. By coating ELISA plates with our “bait”
							and then incubating with “prey” proteins, we can measure attachment through fluorescence or
							ELISA-based analysis. This method is especially valuable for mucin and other glycoproteins,
							with which we cannot perform traditional pull-down assay as they have a very large and
							variable molecular size, and lack a purification tag.</p>
						<p id="62475bc7-90af-4e84-b9ba-f34678954d48" className="">With the appropriate antibodies one could
							perform real ELISA assays, however we were unable to find antibodies which were tested to
							react to sfGFP (even after contacting antibody manufacturers!), and we decided to directly
							measure the fluorescence of the sfGFP subunit instead. With some more resources and time, we
							would like to test the reactivity of anti-GFP antibodies to sfGFP.</p>
					</details>
				</li>
			</ul>
			<ul id="b352f865-a475-48c5-a334-de8f4ae575d3" className="toggle">
				<li>
					<details open="">
						<summary>SDS-PAGE / Native-PAGE</summary>
						<p id="a2356333-ee74-465d-912f-0b299f955f7b" className="">All purified proteins and elutes from
							binding assays are analysed through electrophoresis methods. Native-PAGE retains protein
							structures without denaturing them (unlike SDS-PAGE) and so protein complexes can be
							maintained in their interactive state, potentially even allowing us to visualise protein
							interactions.</p>
					</details>
				</li>
			</ul>
			<p id="647adac1-f880-4cc9-88d7-7bc7a1a26118" className="">
			</p>
			<h2 id="infectivity-assay-text" className="">Infectivity assays</h2>
			<p id="67cb75e8-fdf0-4100-a411-298bdcf75f42">Just generating binding proteins does not necessarily equate to a product which prevents infection. As our secondary proof-of-concept, we target T4 bacteriophage and attempt to reduce its rate of cell entry (into <em><em><em><em><em><em><em>E. coli</em></em></em></em></em></em></em>, which isn’t the most direct comparison to our epithelium, but it’s a decent proof of concept that is available to us in our lab).</p>
			<p id="83fbebae-0351-415d-90ca-3d1e68d792b4">We use T4 as a surrogate for HPV for 3 reasons:</p>
			<ul id="10b16292-4454-4da0-8a70-86a1bda1221f" className="bulleted-list">
				<li style={{ listStyleType: 'disc' }}>It’s a naked virus of similar size to HPV</li>
			</ul>
			<ul id="f83b0523-7a7a-460c-bccf-5b3dcffaefe3" className="bulleted-list">
				<li style={{ listStyleType: 'disc' }}>It forms plaques when exposed to<em> E.coli</em> and can serve as a direct assay for multimeric binding</li>
			</ul>
			<ul id="1a6183ce-417e-4008-954f-59227abb6bba" className="bulleted-list">
				<li style={{ listStyleType: 'disc' }}>We also have a solution of T4 which is tagged with sfGFP (provided to us by Dr Arnaud Gutierrez, Institut Cochin) on its capsid that helps us, both from a screening point of view, and because sfGFP did not evolve to evade recognition from a host’s immune system, hence giving us an option to generate a more effective binding protein targeting the sfGFP as a preliminary test</li>
			</ul>
			<p id="309d9b66-5c8f-4356-baf3-448f4b8805d6">Specific protocols for phage infection after treatment in mucin has not been devised, we attempt a few different methods to quantify the interaction of phage and our proteins.</p>
			<h2 id="mucin-binder-text" className="">Mucin binder selection and flexible linker</h2>
			<p id="23e1ea04-90ee-46fa-913d-891ba6a5b736" className="">An important part of our design is the mucin-binding
				domain, which stabilises our sticky target binding protein in the lubricant meshwork and essentially
				holds everything together. For our proof of concept we analyse three very distinct mucin binding
				proteins: <a href="http://parts.igem.org/part:BBa_K4602002">StcE_X409</a>, the binding domain of a
				mucinase protein; <a href="http://parts.igem.org/Part:BBa_K4602003">hMUC2_CysD</a>, a domain of human
				mucin itself which facilitates some polymerisation; and <a
					href="http://parts.igem.org/Part:BBa_K4602004">LEC_GREGR</a>, a lectin known to bind to mucin family
				proteins. Of course there are many other potential options available, and perhaps we could even design
				our own (although RFdiffusion is not capable of designing glycoproteins, yet) but we hope to establish a
				strong binding profile based on their characteristic differences.</p>
			<p id="1e7fffa0-beff-4d1c-9fa6-de359c5f26c5" className="">Of course the linker region which connects the two
				binding domain is also of importance. The length of the linker and its flexibility can highly affect
				multibinding of the protein and therefore its Koff (refer to <a
					href="https://2023.igem.wiki/paris-bettencourt/model">model</a>). For our experiments we solely use
				our <a href="http://parts.igem.org/Part:BBa_K4602430">TEV-cleavable flexible linker</a>, however after
				establishing ideal binding domains, it is vital to experiment with different linker types as well.</p>
			<h2 id="lubricant-conposition-text" className="">Lubricant composition and lubricity assessment</h2>
			<p id="1af31d80-b627-46e7-954e-c0a48ebb4991" className="">From a product design entrepreneurial perspective, our
				lubricant must also be pleasurable to use! To assess this, we perform material analysis on our prototype
				through rheological and viscosity characterisations. We analyse different lubricant compositions, with
				proteins and at different pH levels (vaginal pH can be as low as 3.8, while anal and skin pH are
				generally close to neutral) and compare them to commercially available personal lubricants, to
				extrapolate the required final product characteristics and adjust accordingly through the addition of
				natural stabilisers and other lubricating agents. (Glycerol is widely used in personal lubricants and
				also has protein stabilising properties, an strong candidate for our lubricant mixture).</p>
			<h2 id="immunogenicity-text" className="">Immunogenicity assessment</h2>
			<p id="c229aaf3-8ba4-49a6-8472-3c7d962d1916" className="">As both a personal pleasure product and potentially a
				medical devise, it is absolutely vital that our product does not induce an immune response in humans.
				With the use of de novo proteins, no one can predict how our bodies might react to these, and our choice
				of mucins and mucin-binding proteins highly affect this as well.</p>
			<p id="3c4ca461-41bd-4e9a-9ccd-6ba6de455d24" className="">For a full analysis of our product, it may be
				necessary to conduct in-depth clinical trials. But there are other immunogenicity experiments we can
				perform in the lab as well. With a general composition of our product established, we can apply them to
				model epithelia and mucosa and assess the level of induced response. See our interview with Dr Morgane
				Bomsel for more information.</p>
			<h2 id="further-kinetics-text" className="">Further kinetics (binding and lubricity)</h2>
			<p id="a8cd741d-74c9-4973-a564-6d16a238d805" className="">Extensive data collection is what we love, and what
				better than to get detailed kinetics data. Although we were unable to reach this point in the timeframe
				of our current project, we hope to further pursue analysis of our designs through more sophisticated
				kinetics assessment. This includes but is not limited to:</p>
			<ul id="bc63da62-8e24-49e7-afb8-45a8cafc6847" className="bulleted-list">
				<li style={{listStyleType:"disc"}}>Microscale thermophoresis (MST)</li>
			</ul>
			<ul id="e72745b7-36c5-46d5-92b1-3f4b97375174" className="bulleted-list">
				<li style={{listStyleType:"disc"}}>Isothermal titration calorimetry (ITC)</li>
			</ul>
			<ul id="dd6e8bd1-d9e9-45ef-8bbd-cdbc553e1aa6" className="bulleted-list">
				<li style={{listStyleType:"disc"}}>Surface plasmon resonance (SPR)</li>
			</ul>
			<ul id="1d1f1c02-6f68-4fd2-8e76-b4caf94328f2" className="bulleted-list">
				<li style={{listStyleType:"disc"}}>Bio-layer interferometry (BLI)</li>
			</ul>
			<h2 id="lubritect-text" className="">Lubritect</h2>
			<p id="100f2d50-2266-44c7-a01c-c858408c5b06" className="">After all of our testing has concluded, we hope to
				have a game-changing product. Providing an enhanced feeling during intimate moments, and an increased
				sense of security that you and your loved ones are protected from infection. With our modular binding
				protein design, we hope to design lubricant products specific to certain pathogens, and even combined
				products which provide general defence against various infections.</p>
			<p id="75a00e5d-5cc6-4911-ac81-376a5c4c2d7b" className="">
			</p>
		</div>
        </div>
);
}

export default Proof;
